import React, { useEffect, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardRouter from './components/DashboardRouter';
import Header from './components/Header';
import SampleComponent from './components/SampleComponent';
import PublicInfoDashboard from './pages/PublicInfoDashboard';
import Login from './pages/Login';
import Register from './pages/Register';
import UserDashboard from './pages/UserDashboard';
import CitizenDashboard from './pages/CitizenDashboard';
import AdminDashboard from './pages/AdminDashboard';
import EditUsersPage from './pages/EditUsersPage';
import StaffDashboard from './pages/StaffDashboard';
import EducationDashboard from './pages/EducationDashboard';
import FileManagerPage from './pages/FileManagerPage';
import BlogPage from './pages/BlogPage';
import BlogManagementPage from './pages/BlogManagementPage';
import StaffFAQManagementPage from './pages/StaffFAQManagementPage';
import HarvestingPage from './pages/HarvestingPage';
import StaffHarvestingPage from './pages/StaffHarvestingPage';
import CreateHarvestingPostPage from './pages/CreateHarvestingPostPage';
import HarvestingEducationPage from './pages/HarvestingEducationPage';
import HarvestingInformationPage from './pages/HarvestingInformationPage';
import HarvestingBlogListPage from './pages/HarvestingBlogListPage';
import HarvestingBlogPostPage from './pages/HarvestingBlogPostPage';
import HarvestingStaticPage from './pages/HarvestingStaticPage';
import HarvestingMapPage from './pages/HarvestingMapPage';
import MyHarvestingPage from './pages/MyHarvestingPage';
import CipEmbedPage from './pages/CipEmbedPage';
import CommunityNewsPage from './pages/CommunityNewsPage';
import FAQPage from './pages/FAQPage';
import CommunityPages from './pages/CommunityPages';
import CommunityPageView from './pages/CommunityPageView';
import ConsultationPage from './pages/ConsultationPage';
import ProjectsMapPage from './pages/ProjectsMapPage';
import LocationPickerPage from './pages/LocationPickerPage';
import PublicProjectsList from './pages/PublicProjectsList';
import PublicProjectView from './pages/PublicProjectView';
import PublicConsultationMap from './pages/PublicConsultationMap';
import DataCollectionPage from './pages/DataCollectionPage';
import UnifiedMapViewPage from './pages/UnifiedMapViewPage';
import DataTypeManagementPage from './pages/DataTypeManagementPage'; // Add data type management
import { AuthProvider } from './context/AuthContext';
import { BetaProvider } from './context/BetaContext';
import './App.css';
import BetaBanner from './components/BetaBanner';
import BetaProtectedRoute from './components/BetaProtectedRoute';
import BetaLoginPage from './pages/BetaLoginPage';
import BetaRegisterPage from './pages/BetaRegisterPage';
import BetaAdminFeedbackPage from './pages/BetaAdminFeedbackPage';
import AdminFeatureTogglePage from './pages/AdminFeatureTogglePage';
import AdminProgramCategoriesPage from './pages/AdminProgramCategoriesPage';
import CommunityResourcesPage from './pages/CommunityResourcesPage';
import StaffCommunityResourcesPage from './pages/StaffCommunityResourcesPage';
import StaffHealthProgramsPage from './pages/StaffHealthProgramsPage';
import CreateHealthProgramPage from './pages/CreateHealthProgramPage';
import EditHealthProgramPage from './pages/EditHealthProgramPage';
import HealthProgramsPage from './pages/HealthProgramsPage';
import HealthProgramApplicationForm from './pages/HealthProgramApplicationForm';
import ReviewHealthApplicationPage from './pages/ReviewHealthApplicationPage';
import HealthApplicationListPage from './pages/HealthApplicationListPage';
import StaffFrontendSettingsPage from './pages/StaffFrontendSettingsPage';
import ApplicantScoresPage from './pages/ApplicantScoresPage';
import OutreachPage from './pages/OutreachPage';
import CouncilPage from './pages/CouncilPage';
import HRJobsPage from './pages/HRJobsPage';
import StaffHRPage from './pages/StaffHRPage';
import StaffHRApplicationsPage from './pages/StaffHRApplicationsPage';
import CreateJobPage from './pages/CreateJobPage';
import CitizenManagementPage from './pages/CitizenManagementPage';
import AccountPage from './pages/AccountPage';
import DeveloperNotesPage from './pages/DeveloperNotesPage';
import ConnectorSettingsPage from './pages/ConnectorSettingsPage';
import IntegrationHubPage from './pages/IntegrationHubPage';
import ComsHubPage from './pages/ComsHubPage';
import RedirectToCipPage from './pages/RedirectToCipPage';
import EmbedAuthPage from './pages/EmbedAuthPage';
import CitizenCardIssueBar from './components/CitizenCardIssueBar';
import { AuthContext } from './context/AuthContext';

const AppAnalyticsTracker = () => {
  const { trackSectionAccess } = useContext(AuthContext);
  const location = useLocation();

  useEffect(() => {
    if (!trackSectionAccess) return;

    const path = location.pathname || '/';
    const section = path.startsWith('/community-resources') ? 'community-resources'
      : path.startsWith('/health-programs') || path.startsWith('/staff-health-programs') || path.startsWith('/apply-health-program') ? 'health-programs'
      : path.startsWith('/harvesting') ? 'harvesting'
      : path.startsWith('/staff') ? 'staff-dashboard'
      : path.startsWith('/admin') ? 'admin-dashboard'
      : path.startsWith('/education') ? 'education'
      : path.startsWith('/community') ? 'community'
      : 'dashboard';

    trackSectionAccess(section, path);
  }, [location, trackSectionAccess]);

  return null;
};

function App() {
  const RedirectToEducationPrograms = () => (
    <Navigate to="/health-programs?category=education" replace />
  );

  const RedirectToEducationProgramDetail = () => {
    const { id } = useParams();
    return <Navigate to={`/health-programs?category=education&program=${id}`} replace />;
  };

  const RedirectToEducationProgramApply = () => {
    const { id } = useParams();
    return <Navigate to={`/apply-health-program/${id}`} replace />;
  };

  const RedirectToEducationProgramEdit = () => {
    const { id } = useParams();
    return <Navigate to={`/edit-education-program/${id}`} replace />;
  };

  const RedirectToApplicantReview = () => {
    const { id } = useParams();
    return <Navigate to={`/review-health-application/${id}?view=applicant`} replace />;
  };

  const RedirectToAdminReview = () => {
    const { id } = useParams();
    return <Navigate to={`/review-health-application/${id}`} replace />;
  };

  return (
    <AuthProvider>
      <BetaProvider>
        <Router>
          <AppAnalyticsTracker />
          <div className="app">
            <BetaBanner />
            <Header />
            <CitizenCardIssueBar />
            <main className="main-content">
              <Routes>
              {/* Beta routes */}
              <Route path="/beta/login" element={<BetaLoginPage />} />
              <Route path="/beta/register" element={<BetaRegisterPage />} />
              <Route path="/beta/admin/feedback" element={<BetaProtectedRoute element={<BetaAdminFeedbackPage />} requireAdmin={true} />} />

              {/* Public routes */}
              <Route path="/" element={<PublicInfoDashboard />} />
              <Route path="/legacy-home" element={<SampleComponent />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/auth/redirect-to-cip" element={<RedirectToCipPage />} />
              <Route path="/embed-auth" element={<EmbedAuthPage />} />
              
              {/* Dashboard router - intelligently routes based on user role */}
              <Route path="/dashboard" 
                element={<ProtectedRoute element={<DashboardRouter />} requireAuth={true} />} 
              />

              {/* Clear-named aliases for dashboard destinations */}
              <Route path="/member-portal" element={<Navigate to="/user-dashboard" replace />} />
              <Route path="/citizen-portal" element={<Navigate to="/citizen-dashboard" replace />} />
              <Route path="/staff-workspace" element={<Navigate to="/staff-dashboard" replace />} />
              <Route path="/admin-console" element={<Navigate to="/admin-dashboard" replace />} />
              
              {/* User routes - require authentication only */}
              <Route path="/user-dashboard" 
                element={<ProtectedRoute element={<UserDashboard />} requireAuth={true} />} 
              />
              <Route path="/account" 
                element={<ProtectedRoute element={<AccountPage />} requireAuth={true} />} 
              />
              <Route path="/dev-notes"
                element={<ProtectedRoute element={<DeveloperNotesPage />} requiredRoles={['staff', 'admin', 'dev']} />}
              />
              <Route path="/coms-hub"
                element={<ProtectedRoute element={<ComsHubPage />} requiredRoles={['staff', 'admin', 'dev']} />}
              />
                <Route path="/admin/connector-settings"
                  element={<ProtectedRoute element={<ConnectorSettingsPage />} requiredRoles={['admin']} />}
                />
                <Route path="/admin/integration-hub"
                  element={<ProtectedRoute element={<IntegrationHubPage />} requiredRoles={['admin']} />}
                />
              
              {/* Citizen Dashboard - requires verified citizen status */}
              <Route path="/citizen-dashboard" 
                element={<ProtectedRoute element={<CitizenDashboard />} requireAuth={true} />} 
              />
              <Route path="/apply-grant" 
                element={<ProtectedRoute element={<RedirectToEducationPrograms />} requireAuth={true} />} 
              />
              <Route path="/apply-grant/:id" 
                element={<ProtectedRoute element={<RedirectToEducationProgramApply />} requireAuth={true} />} 
              />
              
              {/* Application viewing routes - separated by user type for clarity */}
              <Route path="/applications/:id" 
                element={<ProtectedRoute element={<RedirectToApplicantReview />} requireAuth={true} />} 
              />
              <Route path="/admin/applications/:id" 
                element={<ProtectedRoute 
                  element={<RedirectToAdminReview />} 
                  requiredRoles={['admin', 'staff']} 
                  requiredPermissions={['education']} 
                />} 
              />
              
              <Route path="/applications" 
                element={<ProtectedRoute element={<Navigate to="/education-applications" replace />} requiredRoles={['admin', 'staff']} requiredPermissions={['education']} />} 
              />
              
              {/* Staff routes - require staff or admin role */}
              <Route path="/staff-dashboard" 
                element={<ProtectedRoute 
                  element={<StaffDashboard />} 
                  requiredRoles={['staff', 'admin']} 
                />} 
              />

              {/* Staff Outreach route */}
              <Route path="/staff/outreach" 
                element={<ProtectedRoute 
                  element={<OutreachPage />} 
                  requiredRoles={['staff', 'admin']} 
                  requiredPermissions={['communityOutreach']} 
                />} 
              />

              {/* Staff Council route */}
              <Route path="/staff/council" 
                element={<ProtectedRoute 
                  element={<CouncilPage />} 
                  requiredRoles={['staff', 'admin']} 
                  requiredPermissions={['council']} 
                />} 
              />

              {/* Staff HR route */}
              <Route path="/staff/hr" 
                element={<ProtectedRoute 
                  element={<StaffHRPage />} 
                  requiredRoles={['staff', 'admin']} 
                  requiredPermissions={['hr']} 
                />} 
              />
              <Route path="/staff/hr/applications" 
                element={<ProtectedRoute 
                  element={<StaffHRApplicationsPage />} 
                  requiredRoles={['staff', 'admin']} 
                  requiredPermissions={['hr']} 
                />} 
              />
              <Route path="/staff/hr/jobs/new"
                element={<ProtectedRoute
                  element={<CreateJobPage />}
                  requiredRoles={['staff', 'admin']}
                  requiredPermissions={['hr']}
                />}
              />
              <Route path="/staff/hr/jobs/:id/edit"
                element={<ProtectedRoute
                  element={<CreateJobPage />}
                  requiredRoles={['staff', 'admin']}
                  requiredPermissions={['hr']}
                />}
              />

              {/* Admin routes - require admin role */}
              <Route path="/admin-dashboard" 
                element={<ProtectedRoute 
                  element={<AdminDashboard />} 
                  requiredRoles={['admin']} 
                />} 
              />
              <Route path="/admin/cip-embed" 
                element={<ProtectedRoute 
                  element={<CipEmbedPage />} 
                  requiredRoles={['admin']} 
                />} 
              />
              <Route path="/edit-users" 
                element={<ProtectedRoute 
                  element={<EditUsersPage />} 
                  requiredRoles={['admin']} 
                />} 
              />
              <Route path="/admin/citizens" 
                element={<ProtectedRoute 
                  element={<CitizenManagementPage />} 
                  requiredRoles={['admin']} 
                />} 
              />
              
              {/* Community pages - publicly accessible, backend filters by tier */}
              <Route path="/community-news" element={<CommunityNewsPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/community-pages" element={<CommunityPages />} />
              <Route path="/community-pages/:slug" element={<CommunityPageView />} />
              
              {/* Education routes - require education permission or admin */}
              <Route path="/education-dashboard" 
                element={<ProtectedRoute 
                  element={<EducationDashboard />} 
                  requiredRoles={['admin', 'staff']} 
                  requiredPermissions={['education']} 
                />} 
              />
              <Route path="/grants" 
                element={<ProtectedRoute 
                  element={<Navigate to="/staff-health-programs?category=education" replace />} 
                  requiredRoles={['admin', 'staff']} 
                  requiredPermissions={['education']} 
                />} 
              />
              <Route path="/grants/:id" 
                element={<ProtectedRoute 
                  element={<RedirectToEducationProgramDetail />} 
                  requiredRoles={['admin', 'staff']} 
                  requiredPermissions={['education']} 
                />} 
              />
              <Route path="/grants/:id/edit" 
                element={<ProtectedRoute 
                  element={<RedirectToEducationProgramEdit />} 
                  requiredRoles={['admin', 'staff']} 
                  requiredPermissions={['education']} 
                />} 
              />
              <Route path="/create-grant" 
                element={<ProtectedRoute 
                  element={<CreateHealthProgramPage />} 
                  requiredRoles={['admin', 'staff']} 
                  requiredPermissions={['education']} 
                />} 
              />
              <Route path="/quick-add-grant" 
                element={<ProtectedRoute 
                  element={<Navigate to="/create-grant" replace />} 
                  requiredRoles={['admin', 'staff']} 
                  requiredPermissions={['education']} 
                />} 
              />
              <Route path="/file-manager" 
                element={<ProtectedRoute 
                  element={<FileManagerPage />} 
                  requiredRoles={['staff', 'admin']} 
                />} 
              />

              {/* Education page routes - require authentication */}
              <Route path="/education" 
                element={<ProtectedRoute 
                  element={<RedirectToEducationPrograms />} 
                  requireAuth={true} 
                />} 
              />

              {/* Harvesting routes - authenticated access */}
              <Route path="/harvesting" 
                element={<ProtectedRoute element={<HarvestingPage />} requireAuth={true} />} 
              />
              <Route path="/harvesting/map" 
                element={<ProtectedRoute element={<HarvestingMapPage />} requireAuth={true} />} 
              />
              <Route path="/harvesting/my-activity"
                element={<ProtectedRoute element={<MyHarvestingPage />} requireAuth={true} />}
              />
              <Route path="/harvesting/education" 
                element={<ProtectedRoute element={<HarvestingEducationPage />} requireAuth={true} />} 
              />
              <Route path="/harvesting/information" 
                element={<ProtectedRoute element={<HarvestingInformationPage />} requireAuth={true} />} 
              />
              <Route path="/harvesting/blog" 
                element={<ProtectedRoute element={<HarvestingBlogListPage />} requireAuth={true} />} 
              />
              <Route path="/harvesting/blog/:id" 
                element={<ProtectedRoute element={<HarvestingBlogPostPage />} requireAuth={true} />} 
              />
              <Route path="/harvesting/pages/:slug"
                element={<ProtectedRoute element={<HarvestingStaticPage />} requireAuth={true} />}
              />
              <Route path="/harvesting/pages/:parentSlug/:slug"
                element={<ProtectedRoute element={<HarvestingStaticPage />} requireAuth={true} />}
              />

              {/* Staff Harvesting route */}
              <Route path="/staff/harvesting" 
                element={<ProtectedRoute 
                  element={<StaffHarvestingPage />} 
                  requiredRoles={['staff', 'admin']} 
                  requiredPermissions={['harvesting']} 
                />} 
              />
              <Route path="/staff/harvesting/posts/new"
                element={<ProtectedRoute
                  element={<CreateHarvestingPostPage />}
                  requiredRoles={['staff', 'admin']}
                  requiredPermissions={['harvesting']}
                />}
              />
              <Route path="/staff/harvesting/posts/:id/edit"
                element={<ProtectedRoute
                  element={<CreateHarvestingPostPage />}
                  requiredRoles={['staff', 'admin']}
                  requiredPermissions={['harvesting']}
                />}
              />
              <Route path="/staff/harvesting/permits/new-application-form"
                element={<ProtectedRoute
                  element={<CreateHealthProgramPage />}
                  requiredRoles={['staff', 'admin']}
                  requiredPermissions={['harvesting']}
                />}
              />
              <Route path="/staff/harvesting/permit-applications"
                element={<ProtectedRoute
                  element={<HealthApplicationListPage />}
                  requiredRoles={['staff', 'admin']}
                  requiredPermissions={['harvesting']}
                />}
              />

              {/* Blog & FAQ routes */}
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/staff/blog-management" 
                element={<ProtectedRoute 
                  element={<BlogManagementPage />} 
                  requiredRoles={['staff', 'admin']} 
                />} 
              />

              {/* Staff FAQ Management - faqEditor permission or admin (backend enforces) */}
              <Route path="/staff/faq" 
                element={<ProtectedRoute 
                  element={<StaffFAQManagementPage />} 
                  requiredRoles={['staff', 'admin']} 
                />} 
              />

              {/* Staff Consultation route */}
              <Route path="/staff/consultation" 
                element={<ProtectedRoute 
                  element={<ConsultationPage />} 
                  requiredRoles={['staff', 'admin']} 
                  requiredPermissions={['consultation']} 
                />} 
              />
              
              {/* Staff Consultation Map route */}
              <Route path="/staff/consultation/map" 
                element={<ProtectedRoute 
                  element={<ProjectsMapPage />} 
                  requiredRoles={['staff', 'admin']} 
                  requiredPermissions={['consultation']} 
                />} 
              />

              {/* Location Picker route */}
              <Route path="/location-picker" 
                element={<LocationPickerPage />} 
              />
              <Route path="/staff/consultation/location-picker" 
                element={<ProtectedRoute 
                  element={<LocationPickerPage />} 
                  requiredRoles={['staff', 'admin']} 
                  requiredPermissions={['consultation']} 
                />} 
              />

              {/* Unified Data Collection Routes */}
              <Route path="/data/harvesting"
                element={<ProtectedRoute element={<MyHarvestingPage />} requireAuth={true} />}
              />
              <Route path="/data/berry-harvesting"
                element={<ProtectedRoute element={<MyHarvestingPage />} requireAuth={true} />}
              />
              <Route path="/data/:dataTypeId" 
                element={<ProtectedRoute element={<DataCollectionPage />} requireAuth={true} />} 
              />

              {/* Unified Map View - All Data Types */}
              <Route path="/community-map" 
                element={<UnifiedMapViewPage />} 
              />

              {/* Admin Feature Toggle route */}
              <Route path="/admin/feature-toggles" 
                     element={<ProtectedRoute element={<AdminFeatureTogglePage />} requiredRoles={['admin']} />} 
              />

              {/* Admin Program Categories route */}
              <Route path="/admin/program-categories" 
                     element={<ProtectedRoute 
                       element={<AdminProgramCategoriesPage />} 
                       requiredRoles={['staff', 'admin']} 
                       requiredPermissions={['health']} 
                     />} 
              />

              {/* Community Resources routes - publicly accessible, backend tier filters content */}
              <Route path="/community-resources" element={<CommunityResourcesPage />} />
              {/* Public HR Jobs - no auth required, shows jobs marked as public */}
              <Route path="/hr" element={<HRJobsPage />} />
              
              {/* Public Council - no auth required, shows published council info */}
              <Route path="/council" element={<CouncilPage />} />
              
              <Route path="/staff/community-resources" 
                element={<ProtectedRoute 
                  element={<StaffCommunityResourcesPage />} 
                  requiredRoles={['staff', 'admin']} 
                  requiredPermissions={['communityResources']} 
                />} 
              />

              {/* Health Programs routes - publicly accessible, applying requires auth */}
              <Route path="/health-programs" element={<HealthProgramsPage />} />
              <Route path="/apply-health-program/:id" 
                element={<ProtectedRoute 
                  element={<HealthProgramApplicationForm />} 
                  requireAuth={true} 
                />} 
              />
              <Route path="/staff-health-programs" 
                element={<ProtectedRoute 
                  element={<StaffHealthProgramsPage />} 
                  requiredRoles={['staff', 'admin']} 
                />} 
              />
              <Route path="/create-health-program" 
                element={<ProtectedRoute 
                  element={<CreateHealthProgramPage />} 
                  requiredRoles={['staff', 'admin']} 
                  requiredPermissions={['health']} 
                />} 
              />
              <Route path="/edit-health-program/:id" 
                element={<ProtectedRoute 
                  element={<EditHealthProgramPage />} 
                  requiredRoles={['staff', 'admin']} 
                  requiredPermissions={['health']} 
                />} 
              />
              <Route path="/edit-education-program/:id" 
                element={<ProtectedRoute 
                  element={<EditHealthProgramPage />} 
                  requiredRoles={['staff', 'admin']} 
                  requiredPermissions={['education']} 
                />} 
              />
              <Route path="/health-applications" 
                element={<ProtectedRoute 
                  element={<HealthApplicationListPage />} 
                  requiredRoles={['staff', 'admin']} 
                  requiredPermissions={['health']} 
                />} 
              />
              <Route path="/education-applications" 
                element={<ProtectedRoute 
                  element={<HealthApplicationListPage />} 
                  requiredRoles={['staff', 'admin']} 
                  requiredPermissions={['education']} 
                />} 
              />
              <Route path="/review-health-application/:id" 
                element={<ProtectedRoute 
                  element={<ReviewHealthApplicationPage />} 
                  requireAuth={true} 
                />} 
              />

              {/* Consultation routes - publicly accessible, backend filters by tier */}
              <Route path="/consultation" element={<PublicProjectsList />} />
              <Route path="/consultation-map" element={<PublicConsultationMap />} />
              <Route path="/consultation/map" element={<PublicConsultationMap />} />
              <Route path="/consultation/project/:id" element={<PublicProjectView />} />

              {/* Staff Frontend Settings route */}
              <Route path="/staff/frontend-settings" element={<StaffFrontendSettingsPage />} />

              {/* Staff Data Type Management route */}
              <Route path="/staff/data-types" element={<DataTypeManagementPage />} />

              {/* Applicant Scores route */}
              <Route path="/applicant-scores" element={<ApplicantScoresPage />} />

              {/* Fallback for unknown routes */}
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </main>
          </div>
        </Router>
      </BetaProvider>
    </AuthProvider>
  );
}

export default App;
